import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const BAR_HEIGHT = 42;
const CACHE_KEY = 'op_announcement_cache';

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCache(val) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(val)); } catch { /* noop */ }
}

function pageMatches(pages, pathname) {
  if (!pages || pages.includes('all')) return true;
  return pages.some(p => {
    if (p === 'home') return pathname === '/';
    return pathname === '/' + p || pathname.startsWith('/' + p + '/');
  });
}

export default function AnnouncementBar() {
  const cached = readCache();
  const [messages, setMessages]       = useState([]);
  const [currentIdx, setCurrentIdx]   = useState(0);
  const [visible, setVisible]         = useState(true);
  const [loaded, setLoaded]           = useState(false);
  const [reserved, setReserved]       = useState(!!(cached && cached.hasMessages));
  const [reservedBg, setReservedBg]   = useState((cached && cached.bg) || '#10B981');
  const location = useLocation();

  useEffect(() => {
    fetch(`${API}/api/announcements/live`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const filtered = data.filter(msg => {
          if (sessionStorage.getItem(`ann_dismissed_${msg.id}`) === '1') return false;
          return pageMatches(msg.pages, location.pathname);
        });
        setMessages(filtered);
        setCurrentIdx(0);
        setLoaded(true);
        writeCache({ hasMessages: filtered.length > 0, bg: (filtered[0] && filtered[0].background_color) || '#10B981' });
      })
      .catch(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-filter when route changes (page targeting)
  const pathname = location.pathname;
  useEffect(() => {
    setMessages(prev => prev.filter(msg => pageMatches(msg.pages, pathname)));
    setCurrentIdx(0);
  }, [pathname]);

  // Crossfade rotation every 8 seconds when multiple messages
  useEffect(() => {
    if (messages.length <= 1) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIdx(i => (i + 1) % messages.length);
        setVisible(true);
      }, 350);
    }, 8000);
    return () => clearInterval(id);
  }, [messages.length]);

  const handleDismiss = useCallback((msgId) => {
    sessionStorage.setItem(`ann_dismissed_${msgId}`, '1');
    setMessages(prev => {
      const next = prev.filter(m => m.id !== msgId);
      setCurrentIdx(i => Math.min(i, Math.max(0, next.length - 1)));
      return next;
    });
  }, []);

  if (!messages.length) {
    if (reserved && !loaded) {
      // First paint reservation — avoids pop-in shift when a message was live last session.
      return (
        <div
          data-testid="announcement-bar-placeholder"
          style={{
            width: '100%',
            minHeight: BAR_HEIGHT,
            background: reservedBg,
            transition: 'min-height 0.25s ease',
          }}
        />
      );
    }
    return null;
  }

  const msg = messages[currentIdx] || messages[0];

  return (
    <div
      data-testid="announcement-bar"
      style={{
        width: '100%',
        minHeight: BAR_HEIGHT,
        background: msg.background_color || '#10B981',
        color: msg.text_color || '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13.5,
        fontWeight: 500,
        letterSpacing: '0.01em',
        lineHeight: 1.3,
        padding: '0 48px',
        borderRadius: 0,
        transition: 'background-color 0.35s ease',
        zIndex: 200,
      }}
    >
      <div
        data-testid="announcement-bar-text"
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.35s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textAlign: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {msg.emoji && (
          <span style={{ fontSize: 15 }}>{msg.emoji}</span>
        )}
        <span>{msg.text}</span>
        {msg.link_url && msg.link_text && (
          msg.link_url.startsWith('/') ? (
            <Link
              to={msg.link_url}
              data-testid="announcement-bar-link"
              style={{
                color: msg.text_color || '#fff',
                fontWeight: 700,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                marginLeft: 4,
                whiteSpace: 'nowrap',
              }}
            >
              {msg.link_text}
            </Link>
          ) : (
            <a
              href={msg.link_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="announcement-bar-link"
              style={{
                color: msg.text_color || '#fff',
                fontWeight: 700,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                marginLeft: 4,
                whiteSpace: 'nowrap',
              }}
            >
              {msg.link_text}
            </a>
          )
        )}
        {messages.length > 1 && (
          <span style={{ fontSize: 11, opacity: 0.65, marginLeft: 8 }}>
            {currentIdx + 1}/{messages.length}
          </span>
        )}
      </div>

      {msg.dismissible && (
        <button
          data-testid="announcement-bar-dismiss"
          onClick={() => handleDismiss(msg.id)}
          aria-label="Dismiss announcement"
          style={{
            position: 'absolute',
            right: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: msg.text_color || '#fff',
            opacity: 0.75,
            fontSize: 18,
            lineHeight: 1,
            padding: '4px 6px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
